/**
 * terms.ts — Inline glossary marker parser + TermPopover singleton.
 *
 * Two responsibilities held in one module because they are tightly coupled:
 *
 *   1. renderTermsHtml(text) — transforms editorial prose containing
 *      `{{id|visible text}}` markers into safely-escaped HTML where each
 *      marker becomes a <span class="term-marker" data-term="id">…</span>.
 *
 *   2. TermPopover — page-singleton that listens (delegated) to hover/tap
 *      on `.term-marker` elements, opens a positioned popover with the
 *      glossary entry, and dismisses itself on mouse-out, click-outside,
 *      Esc, scroll, or competing popover open. See the editorial brief
 *      for the UX rationale (Wikipedia/SEP/Britannica style).
 *
 * The popover uses a single DOM element appended to <body> and re-positioned
 * on each open. This keeps z-index handling trivial and avoids per-marker
 * cleanup.
 */

import { getGlossarioService } from '@/services/GlossarioService';
import type { Locale } from '@/i18n/locale';
import type { TermineGlossario } from '@/data/glossario';

// ════════════════════════════════════════════════════════════
// 1. INLINE MARKER PARSER
// ════════════════════════════════════════════════════════════

const MARKER_RE = /\{\{([a-z0-9-]+)\|([^}]+)\}\}/g;

/**
 * Escape HTML special characters. Mirrors the helper used in TappaReaderView,
 * duplicated here to keep terms.ts standalone.
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Convert raw editorial text containing `{{id|label}}` markers into HTML.
 * Splits on `\n\n+` to produce paragraph tags, like the existing
 * `paragraphs()` helper used elsewhere in the views.
 */
export function renderTermsParagraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => `<p>${renderTermsInline(p.trim())}</p>`)
    .join('');
}

/**
 * Single-line variant: no paragraph wrapping. Use this for premise statements,
 * objection titles, or anywhere a line break would break the layout.
 */
export function renderTermsInline(text: string): string {
  let result = '';
  let lastIndex = 0;
  for (const match of text.matchAll(MARKER_RE)) {
    const [full, id, label] = match;
    const start = match.index ?? 0;
    result += escapeHtml(text.slice(lastIndex, start));
    result += `<span class="term-marker" data-term="${escapeHtml(id)}" tabindex="0" role="button" aria-label="${escapeHtml(label)} — apri definizione">${escapeHtml(label)}</span>`;
    lastIndex = start + full.length;
  }
  result += escapeHtml(text.slice(lastIndex));
  return result;
}

// ════════════════════════════════════════════════════════════
// 2. TERM POPOVER SINGLETON
// ════════════════════════════════════════════════════════════

const OPEN_DELAY_MS = 400;
const CLOSE_DELAY_MS = 150;
const POPOVER_WIDTH = 480;
const VIEWPORT_PADDING = 12;

class TermPopoverSingleton {
  private root: HTMLDivElement | null = null;
  private currentAnchor: HTMLElement | null = null;
  private openTimer: number | null = null;
  private closeTimer: number | null = null;
  private locale: Locale = 'it';
  private installed = false;

  /**
   * Install delegated listeners on document.body. Idempotent.
   * Call from main.ts after the AppShell mounts, or lazily on first
   * TappaReaderView/ArgumentDetailView mount.
   */
  install(locale: Locale): void {
    this.locale = locale;
    if (this.installed) return;
    this.installed = true;

    document.body.addEventListener('mouseover', this.onMouseOver, { passive: true });
    document.body.addEventListener('mouseout', this.onMouseOut, { passive: true });
    document.body.addEventListener('click', this.onClick);
    document.body.addEventListener('focusin', this.onFocusIn);
    document.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.dismiss, { passive: true });
  }

  setLocale(locale: Locale): void {
    this.locale = locale;
  }

  private onMouseOver = (ev: MouseEvent): void => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const marker = target.closest<HTMLElement>('.term-marker');
    if (marker) {
      this.scheduleOpen(marker);
      return;
    }
    // Inside the popover itself: cancel any pending close
    if (this.root && (target === this.root || this.root.contains(target))) {
      this.cancelClose();
    }
  };

  private onMouseOut = (ev: MouseEvent): void => {
    const target = ev.target as HTMLElement | null;
    const related = ev.relatedTarget as HTMLElement | null;
    if (!target) return;

    const marker = target.closest<HTMLElement>('.term-marker');
    if (marker) {
      // Mouse leaving the marker: schedule close unless heading into the popover
      if (related && this.root && (related === this.root || this.root.contains(related))) {
        return;
      }
      this.scheduleClose();
      return;
    }
    if (this.root && (target === this.root || this.root.contains(target))) {
      // Leaving the popover itself
      if (related && (related === this.currentAnchor || this.currentAnchor?.contains(related))) {
        return;
      }
      this.scheduleClose();
    }
  };

  private onClick = (ev: MouseEvent): void => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const marker = target.closest<HTMLElement>('.term-marker');
    if (marker) {
      // Tap-to-open on touch devices and click-to-pin on desktop
      this.openImmediate(marker);
      ev.stopPropagation();
      return;
    }
    if (this.root && (target === this.root || this.root.contains(target))) {
      // Click inside the popover — keep it open (could be a future link)
      return;
    }
    this.dismiss();
  };

  private onFocusIn = (ev: FocusEvent): void => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const marker = target.closest<HTMLElement>('.term-marker');
    if (marker) this.openImmediate(marker);
  };

  private onKeyDown = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape' && this.currentAnchor) {
      this.dismiss();
    }
  };

  private onScroll = (ev: Event): void => {
    // Ignore scroll inside the popover itself
    const target = ev.target as Node | null;
    if (target && this.root && (target === this.root || this.root.contains(target as Node))) {
      return;
    }
    if (this.currentAnchor) this.dismiss();
  };

  private scheduleOpen(anchor: HTMLElement): void {
    this.cancelClose();
    if (this.currentAnchor === anchor) return;
    if (this.openTimer !== null) window.clearTimeout(this.openTimer);
    this.openTimer = window.setTimeout(() => {
      this.openImmediate(anchor);
    }, OPEN_DELAY_MS);
  }

  private openImmediate(anchor: HTMLElement): void {
    if (this.openTimer !== null) {
      window.clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    this.cancelClose();
    const id = anchor.dataset.term;
    if (!id) return;
    const entry = getGlossarioService().getTermine(id, this.locale);
    if (!entry) {
      // Silent fail — marker references an unknown term. Log for the editor.
      console.warn(`[TermPopover] unknown glossary term: "${id}"`);
      return;
    }
    this.currentAnchor = anchor;
    this.render(entry);
    this.position(anchor);
  }

  private scheduleClose(): void {
    if (this.closeTimer !== null) window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => this.dismiss(), CLOSE_DELAY_MS);
  }

  private cancelClose(): void {
    if (this.closeTimer !== null) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private dismiss = (): void => {
    if (this.openTimer !== null) {
      window.clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    this.cancelClose();
    if (this.root) {
      this.root.classList.remove('term-popover-open');
      this.root.setAttribute('aria-hidden', 'true');
    }
    this.currentAnchor = null;
  };

  private ensureRoot(): HTMLDivElement {
    if (this.root) return this.root;
    const el = document.createElement('div');
    el.className = 'term-popover';
    el.setAttribute('role', 'tooltip');
    el.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el);
    this.root = el;
    return el;
  }

  private render(entry: TermineGlossario): void {
    const root = this.ensureRoot();
    const origine = entry.origine
      ? ` · <span class="term-popover-origin-inline">${escapeHtml(entry.origine)}</span>`
      : '';
    root.innerHTML = `
      <div class="term-popover-arrow"></div>
      <p class="term-popover-definition"><strong class="term-popover-term-inline">${escapeHtml(entry.termine)}</strong> — ${escapeHtml(entry.definizione)}</p>
      <p class="term-popover-explanation">${escapeHtml(entry.spiegazione)}${origine}</p>
    `;
    root.classList.add('term-popover-open');
    root.setAttribute('aria-hidden', 'false');
  }

  private position(anchor: HTMLElement): void {
    const root = this.root;
    if (!root) return;
    const rect = anchor.getBoundingClientRect();
    const popRect = root.getBoundingClientRect();
    const popHeight = popRect.height || 160;

    // Decide above/below based on available space
    const spaceAbove = rect.top;
    const placeBelow = spaceAbove < popHeight + VIEWPORT_PADDING;

    let top = placeBelow ? rect.bottom + 10 : rect.top - popHeight - 10;
    top = Math.max(VIEWPORT_PADDING, top);

    // Horizontal: center on the anchor, clamp to viewport
    let left = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
    left = Math.max(VIEWPORT_PADDING, Math.min(left, window.innerWidth - POPOVER_WIDTH - VIEWPORT_PADDING));

    root.style.top = `${top + window.scrollY}px`;
    root.style.left = `${left + window.scrollX}px`;
    root.style.width = `${POPOVER_WIDTH}px`;
    root.classList.toggle('term-popover-below', placeBelow);

    // Position the arrow horizontally so it points to the anchor center
    const arrow = root.querySelector<HTMLElement>('.term-popover-arrow');
    if (arrow) {
      const anchorCenter = rect.left + rect.width / 2;
      const arrowLeft = Math.max(12, Math.min(anchorCenter - left, POPOVER_WIDTH - 12));
      arrow.style.left = `${arrowLeft}px`;
    }
  }
}

export const termPopover = new TermPopoverSingleton();
